import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiModuleExhibitionist } from '@wix/da-papi-types';
import useModuleData from '@wix/da-gruser-shared/pkg/hooks/useModuleData';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import Slider, { SeeAllSlide } from '@wix/da-ds/pkg/Slider';
import LazyContext from '../../../LazyLoadingImage/LazyContext';
import StandardThumb from '../../../DeviationViews/Thumb/Standard';
import { AllowedIndicators } from '../../../DeviationViews/Thumb/legos/DeviationThumbIndicators';
import AdoptableShopCard from '../../../ShopCard/AdoptableShopCard';
import OriginalsShopCard from '../../../ShopCard/OriginalsShopCard';
import { useCurrentUser } from '../../../utils/hooks/useCurrentUser';
import { ExhibitionistWidgetType, GallectionSourceType } from '../types';
import { useExhibitionistType } from '../hooks';

import s from './ExhibitionistStrip.scss';

const CARD_WIDTH = 328;
const ALLOWED_INDICATORS = AllowedIndicators.Discount;

export interface Props {
  moduleId: number;
}

const ExhibitionistStrip: React.FC<Props> = ({ moduleId }) => {
  const exhibitionistType = useExhibitionistType();
  const shortStrip = exhibitionistType === ExhibitionistWidgetType.STRIP_SHORT;
  const { moduleData } = useModuleData<PapiModuleExhibitionist>(moduleId);
  const { deviations, seeAllUrl, sourceType, hasMore } = moduleData;
  const isAdoptables =
    sourceType === GallectionSourceType.ADOPTABLE_FOR_SALE ||
    sourceType === GallectionSourceType.ADOPTABLE_BOUGHT;
  const isOriginals = sourceType === GallectionSourceType.ORIGINALS_FOR_SALE;
  const isTallStrip = isAdoptables || isOriginals;

  return (
    <LazyContext.Provider value={true}>
      <Slider
        className={c(
          s['slider'],
          isTallStrip && s['slider-tall'],
          shortStrip && s['slider-short'],
        )}
        scrollBy={0.5}
      >
        {deviations.map(deviation => {
          if (isAdoptables) {
            return <AdoptableExhibitionistCard key={deviation.deviationId} deviation={deviation} />
          }
          if (isOriginals) {
            return (
              <OriginalsShopCard
                key={deviation.deviationId}
                deviation={deviation}
                width={CARD_WIDTH}
                allowedIndicators={ALLOWED_INDICATORS}
                showFooterCta={false}
              />
            );
          }

          return (
            <StandardThumb
              key={deviation.deviationId}
              className={s['slider-item']}
              deviation={deviation}
              height={shortStrip ? 180 : 240}
              responsive={false}
            />
          );
        })}
        {hasMore && seeAllUrl && <SeeAllSlide seeAllUrl={seeAllUrl} />}
      </Slider>
    </LazyContext.Provider>
  );
};
ExhibitionistStrip.displayName = 'ExhibitionistStrip';

export default ExhibitionistStrip;


interface AdoptableExhibitionistCardProps {
  deviation: PapiDeviation;
}
const AdoptableExhibitionistCard: React.FC<AdoptableExhibitionistCardProps> = ({ deviation }) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const extraProps = {};
  const adoptable = deviation.adoptable;
  const isOpenForSale = adoptable?.thumbSaleState === 'open';
  extraProps['hidePrice'] = !isOpenForSale;
  extraProps['hideSold'] = true;
  if (!isOpenForSale && adoptable?.owner?.userId === currentUser?.userId) {
    extraProps['detailsRightSlot'] = (
      <BuyButton isFakeButton size="medium" fillContainerWidth rounded>
        {t('common.sell')}
      </BuyButton>
    );
  }

  return (
    <AdoptableShopCard
      deviation={deviation}
      width={CARD_WIDTH}
      allowedIndicators={ALLOWED_INDICATORS}
      {...extraProps}
    />
  );
};
AdoptableExhibitionistCard.displayName = 'AdoptableExhibitionistCard';
